<script lang="ts" setup>
import { computed, watch } from 'vue'
import { useField } from 'vee-validate'
import type { FormField } from '../types/formTypeStructure'
import {
  useResponsiveSize,
  getLocalizedText
} from '../utils/formHelpers'
import FormFieldWrapper from '../FormFieldWrapper.vue'
import { AlertCircle } from "lucide-vue-next";

interface Props {
  field: FormField & { fieldType: 'text' }
  name?: string
  currentLang?: string
  defaultValue?: string
  className?: string
}

const props = defineProps<Props>()

const fieldName = props.name ?? props.field.fieldName.toString()
const sizeComposable = useResponsiveSize()
const size = computed(() => sizeComposable.calculateSize(window.innerWidth))

const emit = defineEmits<{
  'update:modelValue': [value: string];
  'change': [value: string];
  'input': [value: string];
}>();

const {
  value,
  errorMessage,
  handleChange,
  setTouched
} = useField<string>(fieldName, undefined, {
  initialValue: props.defaultValue ?? ''
})

// Émettre les événements quand la valeur change
watch(value, (newValue) => {
  emit('update:modelValue', newValue);
  emit('change', newValue);
  emit('input', newValue);
});

const placeholder = getLocalizedText(props.field.placeholder, props.currentLang ?? 'fr')
const label = getLocalizedText(props.field.label, props.currentLang ?? 'fr')
const isRequired = computed(() => props.field.validations?.some(v => v.validationType === 'required'))

const inputStyle = computed(() => ({
  width: `${size.value.width}px`,
  height: `${size.value.height}px`,
  borderRadius: `${size.value.borderRadius}px`,
  border: '2px solid #04406E',
  color: '#04406E',
  outlineColor: '#04406E',
  padding: '0 12px',
  fontSize: '1rem',
  transition: 'border-color 0.2s',
  background: '#fff'
}))

const baseClasses = 'transition-all focus:ring-2 focus:ring-[#04406E]'
</script>

<template>
  <div class="text-field-container">
    <!-- Label dupliqué au-dessus de l'input -->
    <div class="text-field-label-container">
      <span class="text-field-label-text">
        {{ label }}
        <AlertCircle
          v-if="isRequired"
          :size="16"
          class="inline text-red-500 ml-2"
        />
      </span>
    </div>

    <FormFieldWrapper :field="props.field" :current-lang="props.currentLang">
      <input
        type="text"
        :value="value"
        :placeholder="placeholder"
        :disabled="props.field.disabled"
        @input="(e) => { handleChange((e.target as HTMLInputElement).value); setTouched(true); }"
        @blur="() => setTouched(true)"
        :class="[baseClasses, props.className]"
        :style="inputStyle"
      />
      <p v-if="errorMessage" class="text-sm text-red-500 mt-1">
        {{ errorMessage }}
      </p>
    </FormFieldWrapper>
  </div>
</template>

<style scoped>
.text-field-container {
  position: relative;
  display: block;
  width: 100%;
}

.text-field-label-container {
  position: relative;
  display: block;
  margin-bottom: 0.75rem;
  z-index: 100;
}

.text-field-label-text {
  display: block;
  font-size: 1.125rem;
  font-weight: 700;
  color: #333333;
  line-height: 1.2;
  margin: 0;
  padding: 0;
  position: static !important;
  transform: none !important;
  top: auto !important;
  left: auto !important;
  right: auto !important;
  bottom: auto !important;
  height: auto !important;
  overflow: visible !important;
  text-align: left !important;
  text-overflow: unset !important;
  white-space: normal !important;
  pointer-events: auto !important;
  border: none !important;
  transform-origin: unset !important;
  transition: none !important;
}
</style>